
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import {
  Box,
  Button,
  TextField,
  Typography,
} from "@mui/material";

import { Table } from "../../layout";
import * as actions from "../../api/actions/pos";
import { AppContext } from "../../App";
import { controller, endpoints } from "../../api/actions/api";
import moment from "moment";
import { safeOnComplete } from "../../utils/safeOnComplete";
import { formatCurrency } from "../../hooks/Currency";
import ReportPrintButton from "./ReportPrintButton";

const ProdSellVol = (props) => {
  const { t } = useTranslation();
  const { _store, _getById } = props;
  const { setLoader } = useContext(AppContext);

  const reportRef = useRef(null);

  const [sales, setSales] = useState([]);

  // Period filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const hasPeriod = Boolean(fromDate || toDate);
  const invalidRange = Boolean(
    fromDate && toDate && fromDate > toDate
  );

  // Load sales
  useEffect(() => {
    setSales([]);

    if (!_store?.id) return;

    setLoader(true);

    const onComplete = (res, err) => {
      safeOnComplete(
        (response) => {
          setSales(
            Array.isArray(response.result)
              ? response.result
              : []
          );
        },
        (error) => {
          console.error("Failed to load sells:", error);
          setSales([]);
        }
      )(res, err);

      setLoader(false);
    };

    _getById(
      endpoints.Sell + controller.GetSellsByStore,
      _store.id,
      null,
      onComplete
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_store?.id]);

  // Determine selected and comparison periods
  const periods = useMemo(() => {
    const today = moment();

    // Default: current calendar month vs previous month
    if (!fromDate && !toDate) {
      return {
        currentStart: today.clone().startOf("month"),
        currentEnd: today.clone().endOf("month"),

        previousStart: today
          .clone()
          .subtract(1, "month")
          .startOf("month"),

        previousEnd: today
          .clone()
          .subtract(1, "month")
          .endOf("month"),
      };
    }

    const currentStart = fromDate
      ? moment(fromDate).startOf("day")
      : moment("1900-01-01").startOf("day");

    const currentEnd = toDate
      ? moment(toDate).endOf("day")
      : today.clone().endOf("day");

    const days = Math.max(
      currentEnd.clone().startOf("day").diff(
        currentStart.clone().startOf("day"),
        "days"
      ) + 1,
      1
    );

    const previousEnd = currentStart
      .clone()
      .subtract(1, "millisecond");

    const previousStart = currentStart
      .clone()
      .subtract(days, "days");

    return {
      currentStart,
      currentEnd,
      previousStart,
      previousEnd,
    };
  }, [fromDate, toDate]);

  // Group products and calculate sales for both periods
  const reportRows = useMemo(() => {
    const grouped = new Map();

    const inPeriod = (date, start, end) =>
      date.isBetween(start, end, undefined, "[]");

    sales.forEach((sale) => {
      if (!sale.sellDate) return;

      const saleDate = moment(sale.sellDate);

      if (!saleDate.isValid()) return;

      const isCurrent = inPeriod(
        saleDate,
        periods.currentStart,
        periods.currentEnd
      );

      const isPrevious = inPeriod(
        saleDate,
        periods.previousStart,
        periods.previousEnd
      );

      if (!isCurrent && !isPrevious) return;

      (sale.sellItems || []).forEach((item, index) => {
        if (item.itemType !== 1 && item.itemType !== 2) {
          return;
        }

        // Group products by product ID.
        // Keep discounts as individual entries.
        const key =
          item.itemType === 2
            ? `discount-${sale.id}-${index}`
            : `product-${item.product?.id}`;

        if (item.itemType === 1 && !item.product) {
          return;
        }

        if (!grouped.has(key)) {
          grouped.set(key, {
            name:
              item.itemType === 2
                ? t("discount")
                : item.product.name,

            isDiscount: item.itemType === 2,
            itemSold: 0,
            previousSales: 0,
            currentSales: 0,
          });
        }

        const row = grouped.get(key);

        const qty = Number(item.qty || 0);
        const amount = qty * Number(item.price || 0);

        if (isCurrent) {
          row.currentSales += amount;

          if (!row.isDiscount) {
            row.itemSold += qty;
          }
        }

        if (isPrevious) {
          row.previousSales += amount;
        }
      });
    });

    return Array.from(grouped.values()).map((row) => ({
      name: row.name,

      itemSold: row.isDiscount ? "-" : row.itemSold,

      lastMonthSale: formatCurrency(
        row.previousSales,
        _store
      ),

      currentMonthSale: formatCurrency(
        row.currentSales,
        _store
      ),

      status:
        row.currentSales > row.previousSales
          ? t("high")
          : t("low"),
    }));
  }, [sales, periods, _store, t]);

  // Same columns for screen and print
  const columns = [
    {
      id: "name",
      label: t("productName"),
    },
    {
      id: "itemSold",
      label: t("numberOfItemsSold"),
    },
    {
      id: "lastMonthSale",
      label: hasPeriod
        ? "Previous Period Sales"
        : t("lastMonthSale"),
    },
    {
      id: "currentMonthSale",
      label: hasPeriod
        ? "Selected Period Sales"
        : t("currentMonthSale"),
    },
    {
      id: "status",
      label: t("status"),
    },
  ];

  const tableData = invalidRange ? [] : reportRows;

  return (
    <>
      {/* Date filters and Print Preview button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ mr: 1 }}>
          {t("productsSellVolume")}
        </Typography>

        <TextField
          label={t("fromDate")}
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label={t("toDate")}
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          inputProps={{
            min: fromDate || undefined,
          }}
          InputLabelProps={{ shrink: true }}
          error={invalidRange}
          helperText={
            invalidRange
              ? "To Date must be on or after From Date"
              : ""
          }
        />

        <Button
          variant="outlined"
          onClick={() => {
            setFromDate("");
            setToDate("");
          }}
        >
          {t("clear")}
        </Button>

        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("productsSellVolume")}
        />
      </Box>

      {/* Normal screen table with Search and Pagination */}
      <Table
        ActionComp={() => <div />}
        HeaderComp={() => <div />}
        tblHead={columns}
        tblData={tableData}
      />

      {/* Separate printable report */}
      <Box
        ref={reportRef}
        className="products-sales-volume-print"
        sx={{
          position: "absolute",
          left: "-10000px",
          top: 0,
          width: "1000px",
          backgroundColor: "#fff",
          color: "#000",
          p: 2,
        }}
      >
        
        {/* Report title */}
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          {t("productsSellVolume")}
        </Typography>

        {/* Period comparison */}
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            textAlign: "center",
          }}
        >
          {hasPeriod
            ? `Selected Period: ${periods.currentStart.format(
                "MM/DD/YYYY"
              )} — ${periods.currentEnd.format(
                "MM/DD/YYYY"
              )}`
            : `Current Month: ${periods.currentStart.format(
                "MM/YYYY"
              )}`}
          {" | "}
          {`Previous Period: ${periods.previousStart.format(
            "MM/DD/YYYY"
          )} — ${periods.previousEnd.format(
            "MM/DD/YYYY"
          )}`}
        </Typography>

        {/* Print all rows without Search or Pagination */}
        <Table
          printMode={true}
          ActionComp={() => <div />}
          HeaderComp={() => <div />}
          tblHead={columns}
          tblData={tableData}
        />
      </Box>
    </>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(ProdSellVol);
